import { useEffect, useMemo, useState } from 'react';
import { useShopLang } from '../../context/ShopLangContext.jsx';
import { useTranslation } from 'react-i18next';

const ShopLangToggle = () => {
  const { lang, setLang } = useShopLang();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // Full set of languages supported by the site
  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇦', rtl: true },
    { code: 'zh', label: '中文', flag: '🇨🇳' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'ur', label: 'اردو', flag: '🇵🇰', rtl: true }
  ];

  const [query, setQuery] = useState('');
  const filtered = useMemo(() =>
    languages.filter(l => l.label.toLowerCase().includes(query.toLowerCase())),
  [languages, query]);

  const change = async (lng) => {
    setLang(lng);
    await i18n.changeLanguage(lng);
    localStorage.setItem('lng', lng);
    const rtl = ['ar', 'ur'].includes(lng);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lng);
    setOpen(false);
  };

  // On mount, align with stored or i18n language and set direction
  useEffect(() => {
    const initial = (localStorage.getItem('lng') || i18n.language || 'en').split('-')[0];
    if (initial !== lang) setLang(initial);
    const rtl = ['ar', 'ur'].includes(initial);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed bottom-5 left-5 z-50 select-none">
      <div className="relative">
        {/* FAB */}
        <button
          onClick={() => setOpen(v => !v)}
          className={`w-12 h-12 rounded-full bg-brown text-cream shadow-2xl flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-brown-dark ${open ? 'ring-2 ring-brown/40' : ''}`}
          aria-label="Shop language"
        >
          {/* simple globe icon */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 2a8 8 0 017.45 5H16.9a12.7 12.7 0 00-2.5-4.38A7.97 7.97 0 0112 4zm-2.4.62A10.7 10.7 0 007.1 9H4.55A8 8 0 019.6 4.62zM4.06 11H7c-.03.33-.05.66-.05 1s.02.67.05 1H4.06a8.03 8.03 0 010-2zm.49 4H7.1c.5 1.78 1.3 3.3 2.5 4.38A8 8 0 014.55 15zm7.45 5a7.97 7.97 0 01-2.4-.62A12.7 12.7 0 0114.4 15h3.05A8 8 0 0112 20zm4.9-6h-3.05c.03-.33.05-.66.05-1s-.02-.67-.05-1H16.9a12.7 12.7 0 01.5 2zm2.49-2h-2.94a10.7 10.7 0 00-.5-2h2.54a8.03 8.03 0 01.9 2zm-3.44-4H14.4c-.5-1.78-1.3-3.3-2.5-4.38A8 8 0 0116.95 7z"/>
          </svg>
        </button>

        {/* Panel */}
        {open && (
          <div className="absolute bottom-14 left-0 w-60 max-h-96 overflow-auto rounded-xl border border-black/5 shadow-[0_10px_30px_rgba(0,0,0,.15)] backdrop-blur bg-[#fff9ed]/90 animate-[fadeIn_.18s_ease-out] lang-panel">
            <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
            .lang-panel{scrollbar-width:none;-ms-overflow-style:none}
            .lang-panel::-webkit-scrollbar{width:0;height:0}
            `}</style>
            <div className="p-3 border-b border-black/5 sticky top-0 bg-[#fff9ed]/95">
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search language"
                className="w-full text-sm px-3 py-2 rounded-md bg-white/70 border border-black/10 focus:outline-none focus:ring-1 focus:ring-brown"
              />
            </div>
            <ul className="py-1" dir="ltr">
              {filtered.map((l) => (
                <li key={l.code}>
                  <button
                    onClick={() => change(l.code)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-[15px] hover:bg-white/70 transition ${lang === l.code ? 'bg-white/90 font-semibold' : ''}`}
                  >
                    <span className="text-lg leading-none">{l.flag || '🌐'}</span>
                    <span className={`flex-1 font-normal ${l.rtl ? 'text-right' : 'text-left'} font-sans`} dir={l.rtl ? 'rtl' : 'ltr'}>{l.label}</span>
                    {lang === l.code && (
                      <span className="text-brown">✓</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopLangToggle;
